import { Table } from 'components/core-ui/Table'
import s from './DailyReportsReport.modules.scss'
import { useEffect, useMemo, useState } from 'react'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { useNotification } from 'hooks'
import { getColumns, getFilters } from './helpers'
import { DownloadActionIcon, ExportActionIcon, PrintActionIcon, ViewActionIcon } from 'icons'
import {
  bulkDownload,
  downloadReportCsv,
  downloadDocument,
  getCollectingCompanyOptions,
  getLegalEntitiesOptions,
  getLocationOptions,
  getWasteCategoryOptions
} from '../api'
import { BulkDownloadFilters, DailyReportReportTableItem, TableState } from '../interfaces'
import { BulkDownloadModal } from '../common/BulkDownloadModal'
import { useImmer } from 'use-immer'
import { DetailsModal } from './DetailsModal'
import { PrintPreviewModal } from '../common/PrintPreviewModal'

const DailyReportsReport = () => {
  const [tableItems, setTableItems] = useState<DailyReportReportTableItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [rowDownloadId, setRowDownloadId] = useState<number>()
  const [detailsRow, setDetailsRow] = useState<DailyReportReportTableItem>()
  const [isBulkDownloadModalOpen, setIsBulkDownloadModalOpen] = useState<boolean>(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false)
  const [isPrintPreviewModalOpen, setIsPrintPreviewModalOpen] = useState<boolean>(false)
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const [isPdfBlobLoading, setIsPdfBlobLoading] = useState<boolean>(false)
  const [bulkDownloadFilters, setBulkDownloadFilters] = useImmer<BulkDownloadFilters>({
    locations: [],
    startDate: null,
    endDate: null
  })
  const [tableState, setTableState] = useState<TableState>({
    order: null,
    orderBy: null,
    tableFilters: null
  })

  const openBulkDownloadModal = (): void => setIsBulkDownloadModalOpen(true)
  const closeBulkDownloadModal = (): void => setIsBulkDownloadModalOpen(false)
  const closeDetailsModal = (): void => {
    setIsDetailsModalOpen(false)
    setDetailsRow(null)
  }

  const openPrintPreviewModal = (): void => setIsPrintPreviewModalOpen(true)
  const closePrintPreviewModal = (): void => {
    setIsPrintPreviewModalOpen(false)
    setPdfBlob(null)
  }

  const { showNotification } = useNotification()

  const toolbarActions = useMemo(
    () => [
      {
        label: 'Export Excel',
        icon: <ExportActionIcon />,
        onClick: () => {
          downloadReportCsvQuery.refetch()
        }
      },
      {
        label: 'Download',
        icon: <DownloadActionIcon />,
        onClick: () => openBulkDownloadModal()
      }
    ],
    []
  )

  const { data: legalEntitiesOptions } = getLegalEntitiesOptions(showNotification)

  const { data: shopOptions } = getLocationOptions(showNotification)

  const { data: wasteCategoryOptions } = getWasteCategoryOptions(showNotification)

  const { data: companyOptions } = getCollectingCompanyOptions(showNotification)

  const downloadQuery = downloadDocument(
    showNotification,
    'DAILY',
    rowDownloadId,
    'DAILY_REPORT',
    setRowDownloadId
  )

  const downloadReportCsvQuery = downloadReportCsv(
    showNotification,
    toTableGETUrl({
      url: '/waste-management-system/report/daily-report/csv',
      tableFilters: tableState.tableFilters
    })
  )

  const bulkDownloadQuery = bulkDownload(
    showNotification,
    bulkDownloadFilters.locations?.map(location => `${location.value}`).join(','),
    bulkDownloadFilters.startDate,
    bulkDownloadFilters.endDate,
    closeBulkDownloadModal
  )

  useEffect(() => {
    if (rowDownloadId) {
      downloadQuery.refetch()
    }
  }, [rowDownloadId])

  const columns = useMemo(() => getColumns(), [])

  const filters = useMemo(
    () => getFilters(legalEntitiesOptions, shopOptions, companyOptions, wasteCategoryOptions),
    [legalEntitiesOptions, shopOptions, companyOptions, wasteCategoryOptions]
  )

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/waste-management-system/report/daily-report',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableState({
        order,
        orderBy,
        tableFilters
      })

      setTableItems(toTableRowItems(response.list || []) as DailyReportReportTableItem[])
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor pentru rapoartele zilnice. ${formatErrorMessage(
          error
        )}`,
        type: 'error'
      })
    }
  }

  const downloadBulkDocuments = () => {
    bulkDownloadQuery.refetch()
  }

  const printPDF = async (rowId: number) => {
    try {
      setIsPdfBlobLoading(true)
      openPrintPreviewModal()

      const response = await makeCoreGetRequest(
        `/waste-management-system/daily-report/pdf/${rowId}`,
        {
          responseType: 'blob'
        }
      )

      const blob = new Blob([response], { type: 'application/pdf' })
      setPdfBlob(blob)
    } catch (error) {
      closePrintPreviewModal()
      showNotification({
        message: `A aparut o eroare la generarea raportului PDF. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    } finally {
      setIsPdfBlobLoading(false)
    }
  }

  return (
    <div className={s.mainContainer}>
      <BulkDownloadModal
        isModalOpen={isBulkDownloadModalOpen}
        closeModal={closeBulkDownloadModal}
        filters={bulkDownloadFilters}
        setFilters={setBulkDownloadFilters}
        downloadBulkDocuments={downloadBulkDocuments}
      />
      <DetailsModal
        isModalOpen={isDetailsModalOpen}
        closeModal={closeDetailsModal}
        detailsRow={detailsRow}
      />
      <PrintPreviewModal
        isModalOpen={isPrintPreviewModalOpen}
        closeModal={closePrintPreviewModal}
        pdfBlob={pdfBlob}
        isPdfBlobLoading={isPdfBlobLoading}
      />
      <Table
        hideCheckbox
        orderBy="creationDate"
        order="desc"
        columns={columns}
        items={tableItems}
        rowColorRules={[{ type: 'eq', field: 'isReportSigned', value: false, color: '#FEF8C6' }]}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        shouldResetPage={shouldResetPage}
        setShouldResetPage={setShouldResetPage}
        rowActions={[
          {
            label: 'Print',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#4A148C',
            desktopIcon: <PrintActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => printPDF(rowId),
            hideCondition: { field: 'isCompanyReport', value: null },
            showTooltip: true
          },
          {
            label: 'Detalii',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#1565C0',
            desktopIcon: <ViewActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => {
              setDetailsRow(tableItems.find(item => item.id === rowId))
              setIsDetailsModalOpen(true)
            },
            showTooltip: true
          },
          {
            label: 'Download',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#33691E',
            desktopIcon: <DownloadActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => {
              setRowDownloadId(rowId)
            },
            hideCondition: { field: 'documentNo', value: 0 },
            showTooltip: true
          }
        ]}
        rowActionsWidth="240px"
        toolbarActions={toolbarActions}
        filters={filters}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
      />
    </div>
  )
}

export default DailyReportsReport
